(() => {
  'use strict';
  if (customElements.get('zelloa-loox-reviews')) return;
  class ZelloaReviews extends HTMLElement {
    connectedCallback() {
      if (this.controller || this.startObserver) return;
      this.start = () => {
        this.startObserver?.disconnect();
        this.startObserver = null;
        this.removeEventListener('focusin', this.start);
        this.removeEventListener('pointerdown', this.start);
        this.initialize();
      };
      if ('IntersectionObserver' in window && !window.Shopify?.designMode) {
        this.startObserver = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) this.start();
        }, { rootMargin: '600px 0px' });
        this.startObserver.observe(this);
        this.addEventListener('focusin', this.start);
        this.addEventListener('pointerdown', this.start, { passive: true });
      } else {
        this.frame = requestAnimationFrame(this.start);
      }
    }
    initialize() {
      if (!this.isConnected || this.controller) return;
      this.grid = this.querySelector('[data-zlx-grid]');
      if (!this.grid) return;
      this.controller = new AbortController();
      const on = (target, name, callback, options = {}) => target?.addEventListener(name, callback, { ...options, signal: this.controller.signal });
      if (!this.records) this.records = [...this.grid.children].flatMap(node => node.matches('[data-zlx-card]') ? [node] : node.matches('template[data-zlx-pending]') ? [node.content.firstElementChild] : []).filter(Boolean);
      this.filter = this.querySelector('[data-zlx-filter]');
      this.more = this.querySelector('[data-zlx-showmore]');
      this.initialCount = Math.max(4, Number(this.dataset.initialCount) || 20);
      this.pageSize = Math.max(1, Number(this.dataset.pageSize) || 8);
      this.limit = this.initialCount;
      this.rating = this.filter?.value || 'all';
      this.dataset.enhanced = 'true';
      const filterWrap = this.querySelector('[data-zlx-filter-wrap]');
      if (filterWrap) filterWrap.hidden = false;
      on(this, 'click', event => this.click(event));
      on(this.filter, 'change', () => { this.rating = this.filter.value; this.limit = this.initialCount; this.render(); });
      on(this, 'error', event => { if (event.target.matches('.zlx-photo')) event.target.hidden = true; }, { capture: true });
      on(document, 'keydown', event => {
        if (event.key !== 'Escape') return;
        const details = this.querySelector('.zlx-details[open]');
        if (details && this.contains(document.activeElement)) { details.open = false; details.querySelector('summary')?.focus(); }
      });
      on(document, 'click', event => { if (!this.querySelector('.zlx-details')?.contains(event.target)) this.querySelector('.zlx-details')?.removeAttribute('open'); });
      on(document, 'shopify:block:select', event => {
        const index = this.records.findIndex(card => card.dataset.blockId === String(event.detail?.blockId));
        if (index < 0) return;
        this.rating = 'all';
        if (this.filter) this.filter.value = 'all';
        this.limit = Math.max(this.limit, index + 1);
        this.render();
        this.records[index].scrollIntoView({ block: 'center', behavior: 'instant' });
      });
      // Full reviews have no expansion controls and need no layout measurements.
      if (this.dataset.truncate === 'true') {
        this.measure = () => {
          if (this.measureFrame) return;
          this.measureFrame = requestAnimationFrame(() => {
            this.measureFrame = null;
            this.measureButtons();
          });
        };
        if ('ResizeObserver' in window) {
          this.resize = new ResizeObserver(this.measure);
          this.resize.observe(this.grid);
        }
        on(document.fonts, 'loadingdone', this.measure);
        document.fonts?.ready.then(() => { if (this.isConnected) this.measure(); });
      }
      this.render();
    }
    render() {
      const matching = this.records.filter(card => this.rating === 'all' || card.dataset.rating === this.rating);
      const visible = matching.slice(0, this.limit);
      // First render usually matches the server-rendered cards: skip the detach/re-append relayout.
      const current = this.grid.children;
      if (current.length !== visible.length || visible.some((card, index) => current[index] !== card)) this.grid.replaceChildren(...visible);
      this.querySelector('[data-zlx-empty]').hidden = matching.length > 0;
      this.more.hidden = visible.length >= matching.length;
      const status = this.querySelector('[data-zlx-status]');
      const label = (this.dataset.statusLabel || 'Showing [shown] of [total] featured reviews').replace('[shown]', visible.length).replace('[total]', matching.length);
      if (status.textContent !== label) status.textContent = label;
      this.grid.querySelectorAll('.zlx-photo').forEach(img => { if (img.complete && !img.naturalWidth) img.hidden = true; });
      this.measureButtons();
    }
    measureButtons() {
      if (!this.isConnected || !this.grid) return;
      this.grid.querySelectorAll('[data-zlx-expand]').forEach(button => {
        const text = button.previousElementSibling;
        if (button.getAttribute('aria-expanded') !== 'true') button.hidden = text.scrollHeight <= text.clientHeight + 1;
      });
    }
    click(event) {
      const button = event.target.closest('button');
      if (!button || !this.contains(button) || button.disabled) return;
      if (button.matches('[data-zlx-showmore]')) {
        const previousCount = this.grid.children.length;
        this.limit += this.pageSize;
        this.render();
        if (this.more.hidden) this.grid.children[previousCount]?.focus({ preventScroll: true });
      }
      if (button.matches('[data-zlx-expand]')) {
        const text = button.previousElementSibling;
        const expanded = button.getAttribute('aria-expanded') !== 'true';
        text.classList.toggle('is-expanded', expanded);
        button.setAttribute('aria-expanded', String(expanded));
        button.textContent = expanded ? button.dataset.less : button.dataset.more;
      }
    }
    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      cancelAnimationFrame(this.measureFrame);
      this.measureFrame = null;
      this.startObserver?.disconnect();
      this.startObserver = null;
      this.removeEventListener('focusin', this.start);
      this.removeEventListener('pointerdown', this.start);
      this.controller?.abort();
      this.controller = null;
      this.resize?.disconnect();
    }
  }
  customElements.define('zelloa-loox-reviews', ZelloaReviews);
})();
